import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { App } from "antd5";

import { graphql } from "lib/generated/app-service-gql";
import {
  BidReportQuestionGroupsQuery,
  UpsertBidReportQuestionsMutation,
  UpsertBidReportQuestionsMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { bidQuestionsQueryKey } from "./useBidQuestions";

const upsertBidReportQuestions = graphql(`
  mutation UpsertBidReportQuestions($req: UpsertBidReportQuestionsRequest!) {
    upsertBidReportQuestions(UpsertBidReportQuestionsRequest: $req) {
      groups {
        name
        questions {
          id
          title
          question
          groupName
          order
          teamId
          companyId
          autoQualify
          reportType
        }
      }
    }
  }
`);

export function useUpsertBidReportQuestions(
  options?: UseMutationOptions<
    UpsertBidReportQuestionsMutation,
    unknown,
    UpsertBidReportQuestionsMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();

  const teamId = useAssertCurrentUser().team.id;

  const { message } = App.useApp();
  const { data, ...rest } = useGraphQlMutation(upsertBidReportQuestions, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      await queryClient.invalidateQueries({ queryKey: ["adminBidQuestions"] });

      const reportType = data.upsertBidReportQuestions.groups[0]?.questions[0]?.reportType;
      if (reportType) {
        queryClient.setQueryData<BidReportQuestionGroupsQuery>(
          bidQuestionsQueryKey({ teamId, reportType }),
          { bidReportQuestionGroups: data.upsertBidReportQuestions },
        );
      }
      options?.onSuccess?.(data, vars, ctx);
      message.success("Successfully updated bid questions");
    },
    onError: async (err, vars, ctx) => {
      options?.onError?.(err, vars, ctx);
      message.error(
        "Unable to update your bid questions; please contact us if the problem persists",
      );
    },
  });

  return { data: data?.upsertBidReportQuestions, ...rest };
}
